import { DatabaseIcon } from "lucide-react";

import { stripDummyFields } from "./SchemaEditor/utils";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { CacheControl, ToolChoice } from "@/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getParameters = (declaration: any, required: boolean): any[] => {
  const requiredParameters = declaration?.parameters?.required || [];
  const allParameters = Object.keys(declaration?.parameters?.properties || {});
  return required
    ? requiredParameters
    : allParameters.filter(param => !requiredParameters.includes(param));
};

interface ToolDeclaration {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function Parameter({ param, value }: { param: string; value: any }) {
  return (
    <li key={param} className="pb-1 text-sm">
      <span className="font-semibold text-gray-500">{`${param} (${value?.type})`}</span>
      {value?.enum && (
        <span className="font-mono text-sm font-semibold text-gray-500">
          [{value.enum.join(", ")}]
        </span>
      )}
      {value?.description && (
        <span className="font-sans text-sm italic text-gray-500">
          : {value?.description?.trim()}
        </span>
      )}

      <SubParams
        values={Object.entries(stripDummyFields(value)).filter(
          ([key, _]) =>
            key !== "type" &&
            key !== "enum" &&
            key !== "description" &&
            key !== "required" &&
            key !== "additionalProperties" &&
            key !== "name" &&
            key !== "title",
        )}
      />
    </li>
  );
}

const SubParamItem = ({
  paramKey,
  value,
}: {
  paramKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}) => {
  const isNestedObject = typeof value === "object" && value.type;

  if (isNestedObject) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return <ParamContent paramKey={paramKey} value={value} />;
  }

  return (
    <ul key={paramKey} className="pl-4">
      <li>
        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
        <ParamContent paramKey={paramKey} value={value} />
      </li>
    </ul>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function SubParams({ values }: { values: [string, any][] }) {
  return values.map(([key, value]) => {
    if (!key || value === null) {
      return null;
    }
    return <SubParamItem key={key} paramKey={key} value={value} />;
  });
}

const ParamContent = ({
  paramKey,
  value,
}: {
  paramKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}) => {
  const isNestedObject = typeof value === "object" && value.type;

  if (isNestedObject) {
    return <Parameter param={paramKey} value={value} />;
  }

  if (typeof value === "object") {
    return <SubParams values={Object.entries(value)} />;
  }

  return <>{JSON.stringify(value)}</>;
};

const ParameterList = ({
  declaration,
  required,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  declaration: any;
  required: boolean;
}) => {
  const parameters = getParameters(declaration, required);
  if (parameters.length === 0) {
    return null;
  }

  return (
    <>
      <div className="font-sans text-sm text-gray-900">
        {required ? "Required" : "Optional"} Parameters:
      </div>
      <ul className="py-1 pl-4">
        {parameters.map(param => (
          <Parameter key={param} param={param} value={declaration?.parameters?.properties[param]} />
        ))}
      </ul>
    </>
  );
};

function getCacheLabel(cc: CacheControl | undefined): string | undefined {
  if (!cc) {
    return undefined;
  }
  return cc.ttl === "1h" ? "1h" : "5m";
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FunctionDeclaration = ({ declaration }: { declaration: any }) => {
  const cacheLabel = getCacheLabel(declaration[declaration.type]?.cache_control);
  return (
    <>
      <div className="flex items-center gap-2 font-medium text-gray-900">
        {declaration[declaration.type]?.name}
        {cacheLabel && (
          <BasicTooltip content="Prompt cache breakpoint">
            <span className="flex items-center gap-0.5 rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
              <DatabaseIcon className="h-3 w-3" />
              {cacheLabel}
            </span>
          </BasicTooltip>
        )}
      </div>
      <div className="pl-2 pt-1 font-sans text-sm italic text-gray-500">
        {declaration[declaration.type]?.description}
      </div>
      <div className="pl-2 pt-1 text-gray-600">
        {declaration[declaration.type]?.parameters?.type === "object" ? (
          <>
            <ParameterList declaration={declaration[declaration.type]} required />
            <ParameterList declaration={declaration[declaration.type]} required={false} />
          </>
        ) : (
          <div>{JSON.stringify(declaration[declaration.type], undefined, 2)}</div>
        )}
      </div>
    </>
  );
};

// Helper to get a unique key for a tool declaration
const getToolKey = (declaration: ToolDeclaration, index: number): string => {
  if (declaration.type === "function" && declaration.function?.name) {
    return `function-${declaration.function.name}`;
  }
  if (declaration.name) {
    return `${declaration.type}-${declaration.name}`;
  }
  return `${declaration.type}-${index}`;
};

// Helper to format type name for display (e.g., "function" -> "Functions", "web_search" -> "Web Search")
const formatTypeName = (type: string): string => {
  const formatted = type
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return `${formatted}s`;
};

// Group tools by their type
const groupToolsByType = (tools: ToolDeclaration[]): Record<string, ToolDeclaration[]> =>
  tools.reduce(
    (groups, tool) => {
      const type = tool.type || "unknown";
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(tool);
      return groups;
    },
    {} as Record<string, ToolDeclaration[]>,
  );

export const ToolDeclarationsFormatter = ({
  toolDeclarations,
  toolChoice,
}: {
  toolDeclarations: ToolDeclaration[];
  toolChoice?: ToolChoice;
}) => {
  const groupedTools = groupToolsByType(toolDeclarations);

  return (
    <div className="space-y-2">
      {Object.entries(groupedTools).map(([type, tools]) => (
        <div key={type} className="pl-2">
          <h2 className="text-gray-900">{formatTypeName(type)}</h2>
          <hr />
          <div className="space-y-3 pl-5 pt-2">
            {tools.map((declaration, index) => (
              <div key={getToolKey(declaration, index)}>
                {declaration.type === "function" ? (
                  <FunctionDeclaration declaration={declaration} />
                ) : (
                  <div className="font-medium text-gray-900">
                    {declaration[declaration.type]?.name || declaration.name || type}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {toolChoice && (
        <div className="font-medium text-gray-700">
          {"tool_choice: "}
          {typeof toolChoice === "string" ? (
            <span className="italic">{toolChoice}</span>
          ) : (
            <span className="font-mono italic">{toolChoice.function.name}</span>
          )}
        </div>
      )}
    </div>
  );
};
